import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ttStatsAPI } from '../services/api';
import { Activity, Calendar, MapPin, User } from 'lucide-react';

const GamePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [gameData, setGameData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadGameData(decodeURIComponent(id));
    }
  }, [id]);

  const loadGameData = async (url: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await ttStatsAPI.getGameDetails(url);
      setGameData(data);
    } catch (err) {
      console.error('Error loading game data:', err);
      setError('Failed to load game data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 transition-colors duration-300">Loading game details...</p>
        </div>
      </div>
    );
  }

  if (error || !gameData) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg p-6 text-center transition-colors duration-300">
        <p className="text-red-600 dark:text-red-400 transition-colors duration-300">{error || 'Game data not found'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Game Header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
        <div className="flex items-center gap-4 mb-4">
          <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-xl transition-colors duration-300">
            <Activity className="h-8 w-8 text-purple-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">Match Details</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
              {gameData.date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(gameData.date).toLocaleDateString()}
                </span>
              )}
              {gameData.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {gameData.location}
                </span>
              )}
              {gameData.competition && (
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded transition-colors duration-300">
                  {gameData.competition}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Teams Score */}
        {gameData.team1 && gameData.team2 && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-300">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center flex-1">
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">{gameData.team1}</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-gray-100 mt-2 transition-colors duration-300">
                  {gameData.score1 || '-'}
                </p>
              </div>
              <div className="text-2xl text-gray-400 dark:text-gray-500 transition-colors duration-300">vs</div>
              <div className="text-center flex-1">
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">{gameData.team2}</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-gray-100 mt-2 transition-colors duration-300">
                  {gameData.score2 || '-'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Individual Matches */}
      {gameData.matches && gameData.matches.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors duration-300">Individual Matches</h2>
          
          <div className="space-y-3">
            {gameData.matches.map((match: any, index: number) => {
              const isWin1 = match.sets1 > match.sets2;
              
              return (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400 dark:text-gray-500 transition-colors duration-300" />
                        <Link
                          to={`/player/${encodeURIComponent(match.player1_url || '')}`}
                          className={`font-medium hover:text-blue-600 transition-colors duration-300 ${
                            isWin1 ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {match.player1}
                        </Link>
                        {match.player1_elo && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">({match.player1_elo})</span>
                        )}
                      </div>
                    </div>

                    <div className="px-4">
                      <span className={`text-xl font-bold ${
                        isWin1 ? 'text-green-600' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {match.sets1}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500 mx-2 transition-colors duration-300">:</span>
                      <span className={`text-xl font-bold ${
                        !isWin1 ? 'text-green-600' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {match.sets2}
                      </span>
                    </div>

                    <div className="flex-1 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {match.player2_elo && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">({match.player2_elo})</span>
                        )}
                        <Link
                          to={`/player/${encodeURIComponent(match.player2_url || '')}`}
                          className={`font-medium hover:text-blue-600 transition-colors duration-300 ${
                            !isWin1 ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {match.player2}
                        </Link>
                        <User className="h-4 w-4 text-gray-400 dark:text-gray-500 transition-colors duration-300" />
                      </div>
                    </div>
                  </div>

                  {match.games && match.games.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 transition-colors duration-300">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Set scores:</p>
                      <div className="flex gap-3">
                        {match.games.map((game: string, gIndex: number) => (
                          <span key={gIndex} className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded transition-colors duration-300">
                            {game}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary */}
      {gameData.summary && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-lg p-4 transition-colors duration-300">
          <p className="text-sm text-blue-800 dark:text-blue-300 transition-colors duration-300">{gameData.summary}</p>
        </div>
      )}
    </div>
  );
};

export default GamePage;